import { Button, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import { trackEvent } from 'utils/analyticsUtils'
import SelectInput from 'components/common/SelectInput'
import FormInput from 'components/common/FormInput'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import SuccessIcon from '../../common/icons/Success'
import DazzedHeading24 from '../../common/text/DazzedHeading24'
import DazzedParagraph16 from '../../common/text/DazzedParagraph16'
import OggHeading40 from '../../common/text/OggHeading40'
import useRcDetails from '../useRcDetails'
import DownloadApp from './DownloadApp'
import PatientsOnboardingVideo from './PatientsOnboardingVideo'
import { IPHONE_MODELS, PIXEL_MODELS, SAMSUNG_MODELS, OS_TYPES, formatDeviceDataForApi } from '../deviceConstants'

const useStyles = makeStyles(
  {
    container: {
      height: '100%',
      textAlign: 'center',
      margin: '60px 0',
      padding: ({ isMobile }) => (isMobile ? '0 20px' : 0)
    },
    icon: {
      marginTop: ({ isMobile }) => (isMobile ? 60 : 90)
    },
    title: {
      marginTop: 20
    },
    subtitle: {
      marginBottom: 40
    },
    paragraph: {
      margin: '40px 0',
      lineHeight: '30px'
    },
    devicePicker: {
      margin: '40px auto',
      maxWidth: 640,
      padding: ({ isMobile }) => (isMobile ? '24px 16px' : '32px'),
      borderRadius: 16,
      border: '1px solid var(--border-color-1)',
      backgroundColor: 'var(--bg-color-10)'
    },
    deviceHeading: {
      marginBottom: 8
    },
    deviceSubtitle: {
      marginBottom: 24
    },
    osButtonsRow: {
      marginTop: 16,
      marginBottom: 24
    },
    osButton: {
      borderRadius: 12,
      border: '1px solid var(--border-color-1)',
      padding: '12px 16px',
      width: '100%',
      textTransform: 'none',
      fontFamily: 'Dazzed',
      backgroundColor: 'var(--bg-color-9)',
      color: 'var(--text-color-6)'
    },
    osButtonSelected: {
      borderColor: 'var(--bg-color-7)',
      backgroundColor: 'var(--bg-color-7)',
      color: 'var(--text-color-1)'
    },
    deviceInputs: {
      marginBottom: 24
    },
    otherMessage: {
      marginTop: 16
    },
    deviceActions: {
      marginTop: 16,
      display: 'flex',
      flexDirection: ({ isMobile }) => (isMobile ? 'column' : 'row'),
      alignItems: ({ isMobile }) => (isMobile ? 'stretch' : 'center'),
      justifyContent: ({ isMobile }) => (isMobile ? 'stretch' : 'center'),
      gap: ({ isMobile }) => (isMobile ? 12 : 24)
    },
    skipButton: {
      background: 'none',
      border: 'none',
      color: 'var(--text-color-6)',
      cursor: 'pointer',
      textDecoration: 'underline',
      fontFamily: 'Dazzed',
      fontSize: '14px',
      padding: 0
    },
    errorMessage: {
      color: 'var(--text-color-10)',
      marginTop: 12
    },
    successMessage: {
      color: 'var(--text-color-6)',
      marginTop: 16
    }
  },
  { isMobile }
)

const SignUpSuccess = () => {
  const classes = useStyles({ isMobile })
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { leadId, deviceUpdated, deviceUpdateError, isDeviceUpdating } = useSelector(
    ({ authReducer }) => authReducer.rcRegister
  )

  const rcConfig = useRcDetails()
  const onboardingType = useMemo(() => rcConfig.onboardingType.onboardingTypeKey, [rcConfig])

  const doctorName = useMemo(() => rcConfig.name, [rcConfig])
  const practiceName = useMemo(() => rcConfig.clinicName, [rcConfig])

  const [deviceCaptured, setDeviceCaptured] = useState(false)
  const [showDevicePicker, setShowDevicePicker] = useState(true)
  const [selectedOS, setSelectedOS] = useState(null)
  const [selectedDevice, setSelectedDevice] = useState('')
  const [androidModel, setAndroidModel] = useState('')
  const [selectedAndroidOption, setSelectedAndroidOption] = useState('')
  const [deviceErrorKey, setDeviceErrorKey] = useState(null)
  const [submittedData, setSubmittedData] = useState(null)
  const [completedDeviceData, setCompletedDeviceData] = useState(null)

  const devicePickerRef = useRef(null)
  const hasScrolledRef = useRef(false)

  const osOptions = useMemo(
    () => [
      { value: OS_TYPES.IOS, label: t('pages.rcLanding.success.devicePicker.osSelection.ios') },
      { value: OS_TYPES.ANDROID, label: t('pages.rcLanding.success.devicePicker.osSelection.android') },
      { value: OS_TYPES.OTHER, label: t('pages.rcLanding.success.devicePicker.osSelection.other') }
    ],
    [t]
  )

  const iphoneOptions = useMemo(() => IPHONE_MODELS.map(model => ({ value: model.value, label: model.label })), [])
  const androidOptions = useMemo(() => {
    return [
      ...PIXEL_MODELS.map(model => ({ value: model.value, label: model.label })),
      ...SAMSUNG_MODELS.map(model => ({ value: model.value, label: model.label })),
      { value: 'android-other', label: t('pages.rcLanding.success.devicePicker.androidDevice.options.other') }
    ]
  }, [t])

  const shouldShowDevicePicker = Boolean(leadId) && showDevicePicker && !deviceCaptured
  const showSuccessMessage = deviceCaptured && !!completedDeviceData

  useEffect(() => {
    if (leadId) {
      setDeviceCaptured(false)
      setShowDevicePicker(true)
      setSelectedOS(null)
      setSelectedDevice('')
      setAndroidModel('')
      setDeviceErrorKey(null)
      setSubmittedData(null)
      setCompletedDeviceData(null)
      hasScrolledRef.current = false
    }
  }, [leadId])

  useEffect(() => {
    if (shouldShowDevicePicker && devicePickerRef.current && !hasScrolledRef.current) {
      hasScrolledRef.current = true
      setTimeout(() => {
        devicePickerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }, 500)
    }
  }, [shouldShowDevicePicker])

  useEffect(() => {
    if (doctorName && practiceName) {
      trackEvent('RC landing page - signup completed', {
        doctorName,
        practiceName,
        autoApproved: rcConfig?.approved,
        shipping: rcConfig?.shipping
      })
    }
  }, [doctorName, practiceName, rcConfig])

  useEffect(() => {
    if (deviceUpdateError) {
      setDeviceErrorKey('errorMessage')
      setSubmittedData(null)
    }
  }, [deviceUpdateError])

  useEffect(() => {
    if (deviceUpdated && submittedData && leadId) {
      trackEvent('RC device capture - completed', { ...submittedData, leadId })
      setDeviceCaptured(true)
      setShowDevicePicker(false)
      setCompletedDeviceData(submittedData)
      setSubmittedData(null)
    }
  }, [deviceUpdated, submittedData, leadId])

  const handleOSSelect = os => {
    setSelectedOS(os)
    setSelectedDevice('')
    setAndroidModel('')
    setSelectedAndroidOption('')
    setDeviceErrorKey(null)
  }

  const handleDeviceSubmit = () => {
    if (!leadId) {
      return
    }

    if (!selectedOS) {
      setDeviceErrorKey('errors.selectOS')
      return
    }

    if (selectedOS === OS_TYPES.IOS && !selectedDevice) {
      setDeviceErrorKey('errors.selectIPhone')
      return
    }

    if (selectedOS === OS_TYPES.ANDROID && !(androidModel || '').trim()) {
      setDeviceErrorKey('errors.selectAndroidModel')
      return
    }

    const formattedDevice = formatDeviceDataForApi({
      selectedOS,
      selectedDevice,
      androidModel
    })

    setDeviceErrorKey(null)
    setSubmittedData(formattedDevice)
    dispatch(Actions.updateRcDeviceRequested({ leadId, ...formattedDevice }))
  }

  const handleSkip = () => {
    if (leadId) {
      trackEvent('RC device capture - skipped', { leadId })
    }
    setDeviceCaptured(true)
    setShowDevicePicker(false)
    setSubmittedData(null)
    setCompletedDeviceData(null)
    setDeviceErrorKey(null)
  }

  const isSubmitDisabled =
    isDeviceUpdating ||
    !selectedOS ||
    (selectedOS === OS_TYPES.IOS && !selectedDevice) ||
    (selectedOS === OS_TYPES.ANDROID && !(androidModel || '').trim())

  return (
    <div className={classes.container}>
      <div className={classes.icon}>
        <SuccessIcon />
      </div>

      <OggHeading40 className={classes.title}>{t('pages.rcLanding.success.title')}</OggHeading40>
      <DazzedHeading24 className={classes.subtitle}>{t('pages.rcLanding.success.subtitle')}</DazzedHeading24>

      {shouldShowDevicePicker && (
        <div ref={devicePickerRef} className={classes.devicePicker}>
          <DazzedHeading24 className={classes.deviceHeading}>
            {t('pages.rcLanding.success.devicePicker.heading')}
          </DazzedHeading24>
          <DazzedParagraph16 className={classes.deviceSubtitle}>
            {t('pages.rcLanding.success.devicePicker.subheading')}
          </DazzedParagraph16>

          <Grid container spacing={2} className={classes.osButtonsRow}>
            {osOptions.map(option => (
              <Grid item xs={12} sm={4} key={option.value}>
                <Button
                  variant="outlined"
                  onClick={() => handleOSSelect(option.value)}
                  className={clsx(classes.osButton, option.value === selectedOS && classes.osButtonSelected)}
                >
                  {option.label}
                </Button>
              </Grid>
            ))}
          </Grid>

          {selectedOS === OS_TYPES.IOS && (
            <div className={classes.deviceInputs}>
              <SelectInput
                label={t('pages.rcLanding.success.devicePicker.iosDevice.label')}
                placeholder={t('pages.rcLanding.success.devicePicker.iosDevice.placeholder')}
                value={selectedDevice}
                options={iphoneOptions}
                onChange={({ value }) => setSelectedDevice(value)}
                style={{ bright: true, landing: true, width: '100%' }}
                searchable
              />
            </div>
          )}

          {selectedOS === OS_TYPES.ANDROID && (
            <div className={classes.deviceInputs}>
              <SelectInput
                label={t('pages.rcLanding.success.devicePicker.androidDevice.label')}
                placeholder={t('pages.rcLanding.success.devicePicker.androidDevice.placeholder')}
                value={selectedAndroidOption}
                options={androidOptions}
                onChange={({ value }) => {
                  setSelectedAndroidOption(value)
                  if (value === 'android-other') {
                    setAndroidModel('')
                  } else {
                    const matched = androidOptions.find(opt => opt.value === value)
                    setAndroidModel(matched?.label || '')
                  }
                }}
                style={{ bright: true, landing: true, width: '100%' }}
                searchable
              />
              {selectedAndroidOption === 'android-other' && (
                <div style={{ marginTop: 16 }}>
                  <FormInput
                    title={t('pages.rcLanding.success.devicePicker.androidModel.label')}
                    placeholder={t('pages.rcLanding.success.devicePicker.androidModel.placeholder')}
                    value={androidModel}
                    setValue={setAndroidModel}
                    style={{ bright: true, landing: true }}
                  />
                </div>
              )}
            </div>
          )}

          {selectedOS === OS_TYPES.OTHER && (
            <DazzedParagraph16 className={classes.otherMessage}>
              {t('pages.rcLanding.success.devicePicker.otherMessage')}
            </DazzedParagraph16>
          )}

          {deviceErrorKey && (
            <DazzedParagraph16 className={classes.errorMessage}>
              {t(`pages.rcLanding.success.devicePicker.${deviceErrorKey}`)}
            </DazzedParagraph16>
          )}

          <div className={classes.deviceActions}>
            <PrimaryButton
              label={t('pages.rcLanding.success.devicePicker.submitButton')}
              onClick={handleDeviceSubmit}
              isLoading={isDeviceUpdating}
              disabled={isSubmitDisabled}
              width={isMobile ? '100%' : '220px'}
            />
            <button type="button" className={classes.skipButton} onClick={handleSkip}>
              {t('pages.rcLanding.success.devicePicker.skipButton')}
            </button>
          </div>
        </div>
      )}

      {showSuccessMessage && (
        <DazzedParagraph16 className={classes.successMessage}>
          {t('pages.rcLanding.success.devicePicker.successMessage')}
        </DazzedParagraph16>
      )}

      {onboardingType === 'autoApproval' && (
        <>
          <DazzedParagraph16 className={classes.paragraph} textAlign="center">
            <div>{t('pages.rcLanding.success.autoApproval.paragraph1')}</div>
            <div>{t('pages.rcLanding.success.autoApproval.paragraph2', { doctorName })}</div>
            <div>{t('pages.rcLanding.success.autoApproval.paragraph3')}</div>
          </DazzedParagraph16>
          <DownloadApp />
        </>
      )}
      {onboardingType === 'autoApprovalWithoutShipping' && (
        <>
          <DazzedParagraph16 className={classes.paragraph} textAlign="center">
            {t('pages.rcLanding.success.autoApprovalWithoutShipping.paragraph1')}
          </DazzedParagraph16>
          <DownloadApp />
        </>
      )}
      {onboardingType === 'manualApprovalWithShipping' && (
        <>
          <DazzedParagraph16 className={classes.paragraph} textAlign="center">
            {t('pages.rcLanding.success.manualApprovalWithShipping.paragraph1', { doctorName })}
          </DazzedParagraph16>
          <DazzedParagraph16 className={classes.paragraph} textAlign="center">
            {t('pages.rcLanding.success.manualApprovalWithShipping.paragraph2', { practiceName })}
          </DazzedParagraph16>
        </>
      )}

      {isMobile && <PatientsOnboardingVideo />}
    </div>
  )
}

export default SignUpSuccess
